import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { ChangeHistoryDrawer } from "../ChangeHistoryDrawer";

const displayValue = (value) => value === null || value === undefined || value === "" ? "—" : String(value);

const sourceForEntry = (entry) => {
  if (entry.changeSource === "KIP" || entry.kipWriteJournalId) {
    return { label: "Kip", tone: "bg-sky-50 text-sky-700" };
  }
  if (entry.changeSource === "REVERT") {
    return { label: "Revert", tone: "bg-emerald-50 text-emerald-700" };
  }
  return { label: "Manual edit", tone: "bg-slate-100 text-slate-700" };
};

const revertableEntry = (entry) => Boolean(
  entry.undoable || (!entry.kipWriteJournalId && entry.revertable)
);

const ProductCatalogHistoryDrawer = ({ isOpen, onClose, item, catalogWide = false, onReverted }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [pendingAction, setPendingAction] = useState(null);

  useEffect(() => {
    if (!catalogWide) return undefined;
    const timeout = setTimeout(() => setDebouncedSearchTerm(searchTerm), 400);
    return () => clearTimeout(timeout);
  }, [catalogWide, searchTerm]);

  const loadHistory = async () => {
    if ((!catalogWide && !item?.id) || !isOpen) return;
    setLoading(true);
    try {
      const params = catalogWide
        ? { page: currentPage, limit: 100, search: debouncedSearchTerm }
        : { productId: item.id };
      const response = await axios.get("/api/v1/product-catalog/audit-log", { params });
      setEntries(response.data?.items || []);
      if (catalogWide) setPagination(response.data?.pagination || {});
    } catch (error) {
      toast.error(error.response?.data?.error || "Unable to load Product Catalog history.", { position: "bottom-right" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) loadHistory();
  }, [isOpen, item?.id, catalogWide, currentPage, debouncedSearchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isOpen) return;
    setCurrentPage(1);
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setPendingAction(null);
  }, [isOpen]);

  const confirmRevert = async () => {
    const entry = pendingAction?.entry;
    if (!entry || undoing) return;
    setUndoing(true);
    try {
      await axios.post("/api/v1/product-catalog/audit-log/undo", entry.kipWriteJournalId
        ? { journalId: entry.kipWriteJournalId }
        : { auditId: entry.id });
      toast.success("Catalog change reverted.", { position: "bottom-right" });
      setPendingAction(null);
      await loadHistory();
      if (onReverted) await onReverted();
    } catch (error) {
      toast.error(error.response?.data?.error || "Unable to revert catalog change.", { position: "bottom-right" });
    } finally {
      setUndoing(false);
    }
  };

  const rows = entries.map((entry) => {
    const changes = entry.changes || [];
    const fields = changes.map((change) => change.label);
    const oldValues = changes.map((change) => displayValue(change.oldValue));
    const sku = entry.productSku || item?.supply_sku || "—";
    return {
      id: entry.id,
      when: entry.timestamp,
      subject: sku,
      subjectSubtitle: entry.productName,
      fields,
      changes: changes.map((change) => ({
        key: change.field,
        oldValue: displayValue(change.oldValue),
        newValue: displayValue(change.newValue),
      })),
      source: sourceForEntry(entry),
      by: entry.actorName,
      actions: revertableEntry(entry) ? [{
        key: "revert",
        label: "Revert",
        disabled: undoing,
        onClick: () => setPendingAction({
          label: `Revert ${sku} ${fields.join(", ")} back to ${oldValues.join(", ")}?`,
          entry,
        }),
      }] : [],
    };
  });

  const title = catalogWide
    ? "Product Catalog Change History"
    : `Product Catalog History — ${item?.supply_sku || item?.canonical_product_name || "Product"}`;
  const totalCount = pagination.totalCount || 0;

  return (
    <ChangeHistoryDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      description="Every Product Catalog change is logged here. Revert a single change in one click."
      width={catalogWide ? "900px" : "800px"}
      mobileTopOffset
      searchTerm={catalogWide ? searchTerm : undefined}
      onSearchTermChange={(value) => {
        setSearchTerm(value);
        setCurrentPage(1);
      }}
      searchPlaceholder="Filter by SKU or name…"
      currentPage={currentPage}
      totalPages={catalogWide ? (pagination.totalPages || 1) : null}
      isTheLastPage={Boolean(pagination.isTheLastPage)}
      onPageChange={setCurrentPage}
      totalCountLabel={catalogWide ? `${totalCount} change${totalCount === 1 ? "" : "s"}` : undefined}
      loading={loading}
      emptyText="No Product Catalog changes logged yet."
      rows={rows}
      pendingAction={pendingAction}
      onCancelAction={() => setPendingAction(null)}
      onConfirmAction={confirmRevert}
      actionInFlight={undoing}
    />
  );
};

export { ProductCatalogHistoryDrawer };
export default ProductCatalogHistoryDrawer;
